import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    VStack,
    HStack,
    Spinner,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Grid,
    GridItem,
    Tag,
    useDisclosure,
    Icon,
    IconButton
} from '@chakra-ui/react';
import {
    BiCheckCircle,
    BiXCircle,
    BiTime,
    BiPlay,
    BiSend,
    BiPause
} from 'react-icons/bi';
import {
    BsActivity,
    BsArrowLeft,
    BsBoxArrowUpRight,
    BsClock,
    BsFilter,
} from 'react-icons/bs';
import { FaMobileAlt } from 'react-icons/fa';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import Header from '../../../components/Header';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';
import { useParams, useNavigate } from 'react-router-dom';
import { TimeIcon } from '@chakra-ui/icons';
import Prism from 'prismjs';
import "prismjs/components/prism-json";
import "prismjs/themes/prism-tomorrow.css";
import { fetchWrapper } from '../../../helpers';
import { LuBell, LuInfo, LuMail, LuMessageCircle, LuMessageSquare, LuPhone } from 'react-icons/lu';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';

const Channels = [
    {
        name: "Whatsapp",
        icon: LuMessageCircle,
        color: "#00a63e",
        description: "Rich messaging with media and buttons",
        id: 2,
    },
    {
        name: "SMS",
        icon: LuMessageSquare,
        color: "#155dfc",
        description: "Simple text messages",
        id: 1,
    },
    {
        name: "E-Mail",
        icon: LuMail,
        color: "#9810fa",
        description: "HTML emails with rich content",
        id: 3,
    },
    {
        name: "Push Notification",
        icon: LuBell,
        color: "#e60076",
        description: "Mobile app notifications",
        id: 6,
    },
    {
        name: "In-App Notification",
        icon: LuBell,
        color: "#4f39f6",
        description: "In-app messages and alerts",
        id: 5,
    },
    {
        name: "Voice (IVR)",
        icon: LuPhone,
        color: "#f54a00",
        description: "Automated voice calls",
        id: 4,
    },
];
const getChannelIcon = (channel) => {

    switch (channel) {
        case 'Whatsapp':
            return <LuMessageCircle size={16} style={{ color: "#25D366" }} />;
        case 'E-Mail':
            return <LuMail size={16} style={{ color: "#3b82f6" }} />;
        case 'SMS':
            return <LuMessageSquare size={16} style={{ color: "#9333ea" }} />;
        case 'Push Notification':
            return <LuBell size={16} style={{ color: "#f97316" }} />;
        case 'In-App Notification':
            return <LuBell size={16} style={{ color: "var(--brand-primary)" }} />;
        default:
            return <LuPhone size={16} style={{ color: "var(--brand-primary)" }} />;
    }
};

const getDeliveryStatusTag = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
        case 'sent':
            return (
                <Tag colorScheme="blue" variant="subtle">
                    Sent
                </Tag>
            );
        case 'delivered':
            return (
                <Tag colorScheme="green" variant="subtle">
                    Delivered
                </Tag>
            );
        case 'read':
            return (
                <Tag colorScheme="purple" variant="subtle">
                    Read
                </Tag>
            );
        case 'failed':
            return (
                <Tag colorScheme="red" variant="subtle">
                    Failed
                </Tag>
            );
        default:
            return <Tag variant="subtle">{status}</Tag>;
    }
};

const getNodeStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
        case 'completed':
            return <BiCheckCircle size={16} color="#16a34a" />;
        case 'skipped':
            return <BiPause size={16} color="#9ca3af" />;
        case 'stopped':
        case 'failed':
            return <BiXCircle size={16} color="#6b7280" />;
        default:
            return <BiTime size={16} color="#9ca3af" />;
    }
};

const getNodeTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
        case 'start':
        case 'trigger':
            return <BiPlay size={16} color="var(--brand-primary)" />;
        case 'action':
        case 'message':
            return <BiSend size={16} color="#3b82f6" />;
        case 'wait':
            return <BsClock size={16} color="#f97316" />;
        case 'condition':
            return <BsFilter size={16} color="#9333ea" />;
        case 'end':
            return <BiCheckCircle size={16} color="#16a34a" />;
        default:
            return <BsActivity size={16} color="#c48a34" />;
    }
};

const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};
const flattenObject = (obj, prefix = '') => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            Object.assign(acc, flattenObject(value, fullKey));
        } else {
            acc[fullKey] = value;
        }
        return acc;
    }, {});
};

const ContextTable = ({ data }) => {
    const flat = flattenObject(data);
    const entries = Object.entries(flat);
    return (
        <Table variant="simple" size="xs" border="1px" borderColor="gray.200" style={{ borderCollapse: 'collapse' }}>
            <Thead bg="gray.50">
                <Tr>
                    <Th fontSize="xs" w="45%" borderRight="1px" borderColor="gray.200">Key</Th>
                    <Th fontSize="xs">Value</Th>
                </Tr>
            </Thead>
            <Tbody>
                {entries.map(([key, value]) => (
                    <Tr key={key}>
                        <Td
                            fontFamily="mono"
                            fontSize="xs"
                            color="gray.600"
                            bg="gray.50"
                            verticalAlign="top"
                            borderRight="1px"
                            borderColor="gray.200"
                            px={3}
                            py={2}
                            wordBreak="break-all"
                        >
                            {key}
                        </Td>
                        <Td fontSize="xs" verticalAlign="top" px={3} py={2}>
                            {value === null || value === undefined ? (
                                <Text color="gray.400" fontStyle="italic">null</Text>
                            ) : typeof value === 'boolean' ? (
                                <Tag size="sm" colorScheme={value ? 'green' : 'red'} variant="subtle">{String(value)}</Tag>
                            ) : Array.isArray(value) ? (
                                <Text wordBreak="break-all">{JSON.stringify(value)}</Text>
                            ) : (
                                <Text wordBreak="break-all">{String(value)}</Text>
                            )}
                        </Td>
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );
};

export default function ExecutionDetail() {
    const [loading, setLoading] = useState(false);
    const [executionData, setExecutionData] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const toast = useToast();
    const { id } = useParams();
    const navigate = useNavigate();
    const executionId = id;
    const [isOpen, setIsOpen] = useState(false);
    const [contextModal, setContextModal] = useState({ open: false, context: null });
    const [contextView, setContextView] = useState('table');
    const {
        isOpen: isPayloadOpen,
        onOpen: onPayloadOpen,
        onClose: onPayloadClose,
    } = useDisclosure();
    const [statusList, setStatusList] = useState([]);
    const [selectedPayload, setSelectedPayload] = useState(null);

    const fetchExecutionDetails = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_FLOW_API_URL}/flow/execution-details?execution_id=${executionId}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setExecutionData(response.data);
            }
        } catch (error) {
            console.error('Error fetching execution details:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch execution details',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };


    const getStatusList = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/status/list`;
            const res = await fetchWrapper.get(url);
            setStatusList(res?.data || res || []);
        } catch (err) {
            console.error("Failed to load status list:", err);
        }
    };
    useEffect(() => {
        if (executionId) {
            fetchExecutionDetails();
            getStatusList()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executionId]);



    if (loading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Flex justify="center" align="center" minH="400px">
                        <Spinner size="xl" color="var(--brand-primary)" />
                    </Flex>
                </BoxWrapper>
            </>
        );
    }

    if (!executionData) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Text>No execution data found</Text>
                </BoxWrapper>
            </>
        );
    }

    const { timeline = [], message_logs = [], flow_name, trigger_event, flow_id, execution_id, context } = executionData;
    
   
    const getChannelInfo = (channelIdOrName) => {
        return (
            Channels.find(
                (c) => c.id === channelIdOrName || c.name === channelIdOrName
            ) || { name: "System", icon: FaMobileAlt, color: "gray" }
        );
    };
    // Calculate duration from timeline
    const calculateDuration = () => {
        if (timeline.length < 2) return '0s';
        const start = new Date(timeline[0].timestamp);
        const end = new Date(timeline[timeline.length - 1].timestamp);
        const diffSeconds = Math.floor((end - start) / 1000);

        if (diffSeconds < 60) return `${diffSeconds}s`;
        if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ${diffSeconds % 60}s`;

        const hours = Math.floor(diffSeconds / 3600);
        const minutes = Math.floor((diffSeconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    const onBackToList = () => {
        navigate(-1); // Go back to previous page
    };


    const openModal = (msg) => {

        setSelectedMessage(msg);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setSelectedMessage(null);
    };


    const getTypeId = (typeId) => {
        switch (typeId) {
            case 1:
                return "Authentication";
            case 2:
                return "Transactional";
            case 3:
                return "Marketing";
            case 4:
                return "Informational";
            default:
                return "Unknown";
        }
    }

    const getStatusDetails = (statusId) => {
        return statusList.find((s) => s.status_id === statusId || s.id === statusId);
    };

    const getStatusColor = (statusName) => {
        const name = statusName?.toLowerCase() || "";
        if (name.includes("read") || name.includes("delivered")) return "green";
        if (name.includes("sent")) return "blue";
        if (name.includes("fail") || name.includes("undeliverable")) return "red";
        if (name.includes("accept")) return "green";
        return "gray";
    };
    const regexCheckHtml = /<[^>]*>/;
    const handleViewPayload = (payload) => {
        const parsedPayload = JSON.parse(JSON.stringify(payload, (key, value) => {
            if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
                try {
                    return JSON.parse(value);
                } catch {
                    return value;
                }
            }
            return value;
        }));

        const formatted = JSON.stringify(parsedPayload, null, 2);
        setSelectedPayload(formatted);
        onPayloadOpen();

        setTimeout(() => Prism.highlightAll(), 0);
    };

    const handleViewCanvas = (id) => {
        navigate(`/marketing/flow/${id}`);
    }

    return (
        <>
            <Header />
            <BoxWrapper>
                <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <Flex align="start" justify="space-between">
                        <Flex align="start" gap={4} direction={"column"}>
                            <Button
                                leftIcon={<BsArrowLeft />}
                                variant="outline"
                                size="sm"
                                onClick={onBackToList}
                                _hover={{ bg: "#0000001a !important", color: "black !important" }}
                            >
                                Back to Executions
                            </Button>

                            <Box>
                                <Flex align="center" gap={2}>
                                    <BsActivity size={28} color="var(--brand-primary)" />
                                    <Text fontSize="xl" fontWeight="bold">
                                        Execution Details
                                    </Text>
                                </Flex>
                                <Text fontSize="sm" color="gray.500" mt={1}>
                                    Detailed timeline and message logs for execution {execution_id}
                                </Text>
                            </Box>
                        </Flex>
                    </Flex>

                    {/* Execution Info Bar */}
                    <Box className="box-style-card">
                        <Flex align="center" justify="space-between">
                            <Flex align="center" gap={6} fontSize="sm">
                                <Box>
                                    <Text as="span" color="gray.500">
                                        Execution ID:
                                    </Text>
                                    <Text as="span" ml={2} fontWeight="medium" fontFamily="mono">
                                        {execution_id}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text as="span" color="gray.500">
                                        Flow:
                                    </Text>
                                    <Text as="span" ml={2} fontWeight="medium">
                                        {flow_name}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text as="span" color="gray.500">
                                        Duration:
                                    </Text>
                                    <Text as="span" ml={2}>
                                        {calculateDuration()}
                                    </Text>
                                </Box>
                            </Flex>
                            <Button
                                leftIcon={<BsActivity />}
                                rightIcon={<BsBoxArrowUpRight size={12} />}
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewCanvas(flow_id)}
                            >
                                View on Canvas
                            </Button>
                        </Flex>
                    </Box>

                    {/* Split View */}
                    <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                        {/* Left: Timeline */}
                        <GridItem colSpan={1}>
                            <Box className="box-style-card" h="full">
                                <VStack align="stretch" spacing={4}>
                                    <Flex align="center" gap={2}>
                                        <BsActivity size={16} color="var(--brand-primary)" />
                                        <Text fontWeight="medium">Execution Timeline</Text>
                                    </Flex>

                                    {/* TIMELINE CONTENT */}
                                    {timeline && timeline.length > 0 ? (
                                        <VStack align="stretch" spacing={6}>
                                            {timeline.map((node, index) => (
                                                <Box key={node.node_id} position="relative">
                                                    {/* Connector Line */}
                                                    {index < timeline.length - 1 && (
                                                        <Box
                                                            position="absolute"
                                                            left="19px"
                                                            top="40px"
                                                            w="2px"
                                                            h="calc(100% + 8px)"
                                                            bg={
                                                                node.status === 'completed'
                                                                    ? 'green.200'
                                                                    : 'gray.200'
                                                            }
                                                        />
                                                    )}

                                                    {/* Node */}
                                                    <Flex align="flex-start" gap={3}>
                                                        <Flex
                                                            position="relative"
                                                            zIndex={10}
                                                            w="40px"
                                                            h="40px"
                                                            borderRadius="full"
                                                            border="1px solid"
                                                            borderColor={
                                                                node.status === 'completed'
                                                                    ? '#00c951'
                                                                    : node.status === 'stopped' ||
                                                                        node.status === 'failed'
                                                                        ? 'gray.400'
                                                                        : 'gray.300'
                                                            }
                                                            bg={
                                                                node.status === 'completed'
                                                                    ? '#f0fdf4'
                                                                    : 'gray.50'
                                                            }
                                                            align="center"
                                                            justify="center"
                                                        >
                                                            {getNodeTypeIcon(node.node_type)}
                                                        </Flex>

                                                        <Box flex={1} pt={1}>
                                                            <Flex align="center" gap={2} mb={1}>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    {node.node_name}
                                                                </Text>
                                                                {getNodeStatusIcon(node.status)}
                                                                {node.context && Object.keys(node.context).length > 0 && (
                                                                    <IconButton
                                                                        icon={<LuInfo size={13} />}
                                                                        size="xs"
                                                                        variant="ghost"
                                                                        colorScheme="blue"
                                                                        aria-label="View node context"
                                                                        title="View context"
                                                                        onClick={() => setContextModal({ open: true, context: node.context })}
                                                                    />
                                                                )}
                                                            </Flex>

                                                            <Text fontSize="xs" color="gray.500" mb={1}>
                                                                {formatTimestamp(node.timestamp)}
                                                            </Text>

                                                            {node.details && (
                                                                <Box
                                                                    display="inline-block"
                                                                    fontSize="xs"
                                                                    color="gray.600"
                                                                    bg="gray.50"
                                                                    px={2}
                                                                    py={1}
                                                                    borderRadius="md"
                                                                >
                                                                    {node.details}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Flex>
                                                </Box>
                                            ))}
                                        </VStack>
                                    ) : (
                                        /* EMPTY STATE */
                                        <Flex
                                            direction="column"
                                            align="center"
                                            justify="center"
                                            py={10}
                                            color="gray.400"
                                        >
                                            <BsActivity size={28} />
                                            <Text fontSize="sm" mt={2}>
                                                No execution timeline found
                                            </Text>
                                            <Text fontSize="xs">
                                                This flow has any timeline yet
                                            </Text>
                                        </Flex>
                                    )}
                                </VStack>
                            </Box>
                        </GridItem>


                        {/* Right: Message Logs */}
                        <GridItem colSpan={2}>
                            <Box className="box-style-card" h="full">
                                <VStack align="stretch" spacing={4}>
                                    <Flex align="center" gap={2}>
                                        <BiSend size={16} color="var(--brand-primary)" />
                                        <Text fontWeight="medium">Message Log</Text>
                                    </Flex>

                                    {message_logs.length === 0 ? (
                                        <Flex
                                            direction="column"
                                            align="center"
                                            justify="center"
                                            py={12}
                                            textAlign="center"
                                        >
                                            <BiSend size={48} color="#d1d5db" />
                                            <Text color="gray.500" mt={3}>
                                                No messages sent yet
                                            </Text>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Messages will appear here as they are sent
                                            </Text>
                                        </Flex>
                                    ) : (
                                        <Box overflowX="auto">
                                            <Table variant="simple" size="sm">
                                                <Thead bg="gray.50">
                                                    <Tr>
                                                        <Th>Message ID</Th>
                                                        <Th>Channel</Th>
                                                        <Th>Template</Th>
                                                        <Th>Sent To</Th>
                                                        <Th>Status</Th>
                                                        <Th textAlign="right">Action</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {message_logs.map((log) => (
                                                        <Tr key={log.message_id}>
                                                            <Td
                                                                fontSize="xs"
                                                                fontFamily="mono"
                                                                maxW="150px"
                                                                overflow="hidden"
                                                                textOverflow="ellipsis"
                                                            >
                                                                <Text onClick={() => openModal(log)} cursor={"pointer"} color={"var(--brand-primary)"}>{log.message_id}</Text>
                                                            </Td>
                                                            <Td>
                                                                <Flex align="center" gap={2}>
                                                                    {getChannelIcon(log.channel)}
                                                                    <Text
                                                                        fontSize="sm"
                                                                        textTransform="capitalize"
                                                                    >
                                                                        {log.channel}
                                                                    </Text>
                                                                </Flex>
                                                            </Td>
                                                            <Td fontSize="sm">{log.template}</Td>
                                                            <Td fontSize="sm">{log.sent_to}</Td>
                                                            <Td>
                                                                {getDeliveryStatusTag(log.status_value)}
                                                            </Td>

                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    )}
                                </VStack>
                            </Box>
                        </GridItem>
                    </Grid>
                </VStack >
            </BoxWrapper >

            <Modal isOpen={isOpen} onClose={closeModal} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent maxW="600px">
                    <ModalHeader
                        borderBottom="1px"
                        borderColor="gray.200"
                        pb={3}
                        fontSize="lg"
                        fontWeight="semibold"
                    >
                        <HStack spacing={2}>
                            <Icon as={TimeIcon} color="blue.500" />
                            <Text>Message Delivery Journey</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody py={4}>
                        {selectedMessage ? (
                            <VStack align="stretch" spacing={4}>
                                {/* Message ID */}
                                <Box bg="gray.50" borderRadius="md">
                                    <Box bg="red.50" boxShadow="sm" borderRadius="lg" border={1} borderColor="red.200" borderStyle="solid" mb={2}>
                                        <Text fontSize="xs" color="black" p={2}>
                                            <Flex justifyContent={"space-between"} alignItems={"center"}>
                                                <strong> Message Id: {selectedMessage.message_id || selectedMessage._id}</strong>
                                                <Button size={"xs"} bg={"rgb(var(--brand-primary-rgb) / 0.902)"} color={"white"} onClick={() => handleViewPayload(selectedMessage.customer_payload)}>View Payload</Button>
                                            </Flex>
                                        </Text>
                                    </Box>
                                    <Text fontSize="sm" color="gray.600">
                                        Complete delivery timeline showing channel-specific status
                                        progression and auto-failover attempts.
                                    </Text>
                                </Box>

                                {/* Info Grid */}
                                <Box
                                    bg="blue.50"
                                    border="1px"
                                    borderColor="blue.200"
                                    borderRadius="md"
                                    p={3}
                                >
                                    <Flex justify="space-between" mb={2}>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>
                                                Recipient
                                            </Text>
                                            <Text fontSize="xs" fontWeight="semibold">
                                                {selectedMessage.to || "-"}
                                            </Text>
                                        </Box>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>
                                                Message Type
                                            </Text>
                                            <Tag fontSize="xs" fontWeight="normal" border={"1px"} borderColor="gray.300"
                                                color={"black"}
                                                bg="white">
                                                {getTypeId(selectedMessage.message_type_id)}

                                            </Tag>
                                        </Box>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>
                                                Final Status
                                            </Text>
                                            <Tag
                                                colorScheme={getStatusColor(selectedMessage?.Status?.name)}
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                            >
                                                {selectedMessage?.Status?.name || "Unknown"}
                                            </Tag>
                                        </Box>


                                    </Flex>
                                    <Box>
                                        {selectedMessage.message && (
                                            <>
                                                <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">
                                                    Message Content
                                                </Text>
                                                <Box bg="white" border={1} borderColor={"gray.100"} p={3} borderRadius="md">
                                                    {regexCheckHtml.test(selectedMessage.message) ? <Box
                                                        maxH="400px"
                                                        overflowY="auto"
                                                        borderRadius="md"
                                                    >
                                                        <iframe
                                                            srcDoc={selectedMessage.message}
                                                            title="HTML Message"
                                                            style={{
                                                                width: "100%",
                                                                height: "auto",
                                                                border: "none",
                                                                borderRadius: "6px",

                                                            }}
                                                            sandbox="allow-same-origin"
                                                        />
                                                    </Box>
                                                        : <Text fontSize="sm">{selectedMessage.message}</Text>}

                                                </Box>
                                            </>
                                        )}

                                    </Box>

                                </Box>



                                <Box>
                                    <HStack spacing={2} mb={3}>
                                        <Icon as={TimeIcon} color="blue.500" boxSize={4} />
                                        <Text fontSize="sm" fontWeight="semibold">
                                            Delivery Timeline & Status Progression
                                        </Text>
                                    </HStack>

                                    <VStack align="stretch" spacing={0} pl={2}>
                                        {selectedMessage.history?.map((h, i) => {
                                            const status = getStatusDetails(h.status_id);


                                            const { name: channelName, icon: ChannelIcon, color: channelColor } = getChannelInfo(h.channel_id);



                                            const isFailed = status?.name?.toLowerCase().includes("fail") ||
                                                status?.name?.toLowerCase().includes("undeliverable");


                                            const currentIndex = selectedMessage.history.findIndex((item) => item === h);
                                            const nextStatus = selectedMessage.history[currentIndex + 1];
                                            const nextStatusDetails = nextStatus ? getStatusDetails(nextStatus.status_id) : null;

                                            const isNextNotFailed =
                                                nextStatusDetails &&
                                                !nextStatusDetails?.name?.toLowerCase().includes("fail") &&
                                                !nextStatusDetails?.name?.toLowerCase().includes("undeliverable");


                                            const shouldTriggerFailover = isFailed && isNextNotFailed;


                                            const getCooloredStatus = (status) => {
                                                if (!status) return "white";
                                                const name = status.name?.toLowerCase() || "";

                                                if (name.includes("read") || name.includes("delivered")) return "#f0fdf4";
                                                if (name.includes("sent")) return "var(--brand-tint)";
                                                if (name.includes("clicked")) return "#f0fdf4";
                                                if (name.includes("fail") || name.includes("undeliverable")) return "#fef2f2";
                                                if (name.includes("invalid") || name.includes("undeliverable")) return "#fef2f2";
                                                if (name.includes("accept")) return "white";
                                                return "white";
                                            }
                                            const getCooloredBorderedStatus = (status) => {
                                                if (!status) return "gray";
                                                const name = status.name?.toLowerCase() || "";

                                                if (name.includes("read") || name.includes("delivered")) return "#00c951";
                                                if (name.includes("clicked") || name.includes("delivered")) return "#00c951";
                                                if (name.includes("sent")) return "#2b7fff";
                                                if (name.includes("fail") || name.includes("undeliverable")) return "#fb2c36";
                                                if (name.includes("invalid") || name.includes("undeliverable")) return "#fb2c36";
                                                if (name.includes("accept")) return "#6a7282";
                                                return "gray";
                                            }

                                            return (
                                                <Flex key={i}>
                                                    <Box
                                                        flex="1"
                                                        bg={getCooloredStatus(status)}
                                                        border="1px"
                                                        borderColor={getCooloredStatus(status)}
                                                        borderLeftWidth="4px"
                                                        borderLeftColor={getCooloredBorderedStatus(status)}
                                                        borderRadius="md"
                                                        my={3}
                                                        px={2}
                                                        py={5}
                                                    >
                                                        {/* ✅ Left icon + Right content */}
                                                        <Flex align="start" gap={3}>
                                                            {/* Left icon */}
                                                            {status.msg_status === "1" ? (
                                                                <FiCheckCircle color="#00a63e" boxSize={4} />
                                                            ) : (
                                                                <FiXCircle color="#e7000b" boxSize={4} />
                                                            )}

                                                            {/* Right content */}
                                                            <Box flex="1">
                                                                <Flex justify="space-between" align="start" mb={1}>
                                                                    <HStack spacing={2}>
                                                                        {h.channel_id ? (
                                                                            <HStack spacing={1}>
                                                                                <Icon as={ChannelIcon} color={`${channelColor}`} boxSize={3.5} />
                                                                                <Text fontSize="xs" fontWeight="semibold">
                                                                                    {channelName}
                                                                                </Text>
                                                                            </HStack>
                                                                        ) : (
                                                                            <Text fontSize="xs" fontWeight="semibold">
                                                                                {channelName}
                                                                            </Text>
                                                                        )}
                                                                        {status.msg_status === "0" && <Tag bg={"red"} size={"sm"} color={"white"}>Failed</Tag>}
                                                                        <Tag
                                                                            size="sm"
                                                                            border="1px solid"
                                                                            borderColor="gray.300"
                                                                            color={status.name?.toLowerCase().includes("accept") ? "black" : "#64748b"}
                                                                            bg="transparent"
                                                                        >
                                                                            {status?.name || `Status ${h.status_id}`}
                                                                        </Tag>
                                                                    </HStack>

                                                                    <Text fontSize="xs" color="gray.500">
                                                                        {new Date(h.time).toLocaleString("en-PK", {
                                                                            dateStyle: "short",
                                                                            timeStyle: "medium",
                                                                        })}
                                                                    </Text>
                                                                </Flex>
                                                                {(h.channel_id === 5 || h.channel_id === 6) && (
                                                                    <>
                                                                        {h.subscriber_id && (
                                                                            <Text fontSize="xs" color="gray.600">Subscriber Id : {h.subscriber_id}</Text>

                                                                        )}
                                                                    </>
                                                                )}
                                                                <Text fontSize="xs" color="gray.600">
                                                                    {status?.description ||
                                                                        (isFailed
                                                                            ? "Message Undeliverable - Customer not on platform"
                                                                            : status?.name === "Message Accepted"
                                                                                ? "Message Accepted - Request received and validated"
                                                                                : status?.name === "Message Sent"
                                                                                    ? `Message Sent to ${channelName}`
                                                                                    : status?.name === "Message Delivered"
                                                                                        ? "Message Delivered"
                                                                                        : status?.name === "Message Read"
                                                                                            ? "Message Read"
                                                                                            : "")}
                                                                </Text>

                                                                {shouldTriggerFailover && (
                                                                    <Text
                                                                        fontSize="xs"
                                                                        color="#ca3500"
                                                                        bg="#ffedd4"
                                                                        px={2}
                                                                        py={1}
                                                                        mt={2}
                                                                        borderRadius="md"
                                                                        fontWeight="medium"
                                                                    >
                                                                        → Triggering auto-failover to next channel
                                                                    </Text>
                                                                )}
                                                            </Box>
                                                        </Flex>
                                                    </Box>
                                                </Flex>
                                            );
                                        })}
                                    </VStack>
                                </Box>
                            </VStack>
                        ) : (
                            <Text>No details available.</Text>
                        )}
                    </ModalBody>

                    <ModalFooter borderTop="1px" borderColor="gray.200">
                        <Button onClick={closeModal} size={"sm"} bg="var(--brand-primary)"

                            color="white">
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>



            <Modal isOpen={isPayloadOpen} onClose={onPayloadClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Payload Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedPayload ? (
                            <Box
                                maxH="400px"
                                overflowY="auto"
                                // bg="gray.900"
                                color="green.200"
                                // p={4}
                                borderRadius="md"
                            >
                                <pre
                                    style={{
                                        backgroundColor: "black",
                                        textShadow: "none",
                                        color: "white",
                                        padding: "0px",
                                        borderRadius: "8px",
                                        fontFamily: "monospace",

                                    }}
                                >
                                    <span
                                        style={{
                                            padding: 15,
                                            backgroundColor: "#2F2F2F",
                                            fontSize: "14px",
                                            display: "block",
                                            marginBottom: "10px",

                                        }}
                                    >
                                        json
                                    </span>

                                    <code
                                        className="language-json"
                                        style={{ padding: "0px 15px 15px", display: "block", whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{ __html: Prism.highlight(selectedPayload, Prism.languages.json, 'json') }}
                                    />
                                </pre>

                            </Box>
                        ) : (
                            <Text>No payload found.</Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onPayloadClose} size={"sm"} bg="var(--brand-primary)"

                            color="white">Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Trigger Context Modal */}
            <Modal isOpen={contextModal.open} onClose={() => { setContextModal({ open: false, context: null }); setContextView('table'); }} size="3xl" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="sm" pb={2} borderBottom="1px" borderColor="gray.200">
                        <Flex align="center" justify="space-between" pr={8}>
                            <HStack spacing={2}>
                                <LuInfo size={16} color="var(--brand-primary)" />
                                <Text>Node Context</Text>
                            </HStack>
                            <HStack spacing={1}>
                                <Button
                                    size="xs"
                                    variant={contextView === 'table' ? 'solid' : 'outline'}
                                    bg={contextView === 'table' ? 'var(--brand-primary)' : undefined}
                                    color={contextView === 'table' ? 'white' : undefined}
                                    onClick={() => setContextView('table')}
                                >
                                    Table
                                </Button>
                                <Button
                                    size="xs"
                                    variant={contextView === 'json' ? 'solid' : 'outline'}
                                    bg={contextView === 'json' ? 'var(--brand-primary)' : undefined}
                                    color={contextView === 'json' ? 'white' : undefined}
                                    onClick={() => { setContextView('json'); setTimeout(() => Prism.highlightAll(), 0); }}
                                >
                                    JSON
                                </Button>
                            </HStack>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={4}>
                        <Box maxH="500px" overflowY="auto">
                            {contextModal.context && contextView === 'table' && (
                                <ContextTable data={contextModal.context} />
                            )}
                            {contextModal.context && contextView === 'json' && (
                                <pre style={{ backgroundColor: "black", textShadow: "none", color: "white", padding: "0px", borderRadius: "8px", fontFamily: "monospace" }}>
                                    <span style={{ padding: 15, backgroundColor: "#2F2F2F", fontSize: "14px", display: "block", marginBottom: "10px" }}>json</span>
                                    <code
                                        className="language-json"
                                        style={{ padding: "0px 15px 15px", display: "block", whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{ __html: Prism.highlight(JSON.stringify(contextModal.context, null, 2), Prism.languages.json, 'json') }}
                                    />
                                </pre>
                            )}
                        </Box>
                    </ModalBody>
                    <ModalFooter borderTop="1px" borderColor="gray.200">
                        <Button size="sm" bg="var(--brand-primary)" color="white" onClick={() => { setContextModal({ open: false, context: null }); setContextView('table'); }}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}